"use client";

import { Input } from "components/ui/v2";
import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import useTranslation from "next-translate/useTranslation";
import { ChevronRight, Search } from "lucide-react";
import { CIRCLE_ACTION_VARIANT_STYLES } from "components/ui/v2/circle-action-variants";
import { useMobileSidebarStore } from "stores/sidebar/useMobileSidebarStore";
import { useSidebarNavigation } from "./use-sidebar-navigation";
import type { SidebarNavItem } from "./types";

const getToneVars = (tone: SidebarNavItem["tone"]) => {
  if (!tone) return undefined;
  const style = CIRCLE_ACTION_VARIANT_STYLES[tone];

  return {
    "--sidebar-nav-accent": style.accentColor,
    "--sidebar-nav-accent-dark": style.accentColorDark,
    "--sidebar-nav-child-accent": `color-mix(in srgb, ${style.accentColor} 88%, #020617)`,
    "--sidebar-nav-child-accent-dark": `color-mix(in srgb, ${style.accentColorDark} 90%, #000000)`,
    "--sidebar-nav-shadow-rgb": style.shadowRgb
  } as React.CSSProperties;
};

const SidebarNavIcon = ({
  icon,
  active = false,
  compact = false,
  dense = false,
  tone
}: {
  icon: React.ReactNode;
  active?: boolean;
  compact?: boolean;
  dense?: boolean;
  tone?: SidebarNavItem["tone"];
}) => {
  const boxClass = dense
    ? compact
      ? "h-5 w-5 rounded-md"
      : "h-6 w-6 rounded-lg"
    : compact
      ? "h-6 w-6 rounded-lg"
      : "h-8 w-8 rounded-xl";

  const innerClass = dense
    ? compact
      ? "h-3.5 w-3.5"
      : "h-4 w-4"
    : compact
      ? "h-4 w-4"
      : "h-5 w-5";

  return (
    <div
      className={`sidebar-nav-icon-slot flex shrink-0 items-center justify-center bg-slate-100/90 dark:bg-zinc-800/90 ${boxClass} ${
        active ? "ring-1 ring-[color:var(--sidebar-nav-accent)]/30" : ""
      }`}
      style={getToneVars(tone)}
    >
      <div
        className={`flex items-center justify-center ${innerClass} ${
          active
            ? compact
              ? "text-[color:var(--sidebar-nav-child-accent)] dark:text-[color:var(--sidebar-nav-child-accent-dark)]"
              : "text-[color:var(--sidebar-nav-accent)] dark:text-[color:var(--sidebar-nav-accent-dark)]"
            : tone
              ? compact
                ? "text-[color:var(--sidebar-nav-child-accent)]/85 dark:text-[color:var(--sidebar-nav-child-accent-dark)]/85"
                : "text-[color:var(--sidebar-nav-accent)]/75 dark:text-[color:var(--sidebar-nav-accent-dark)]/75"
              : "text-slate-500 dark:text-zinc-400"
        }`}
      >
        {icon}
      </div>
    </div>
  );
};

// ─── NavButton ────────────────────────────────────────────────────────────────

const NavButton = ({
  item,
  isCurrentPath,
  handleNavigation,
  className = "",
  size = "default",
  dense = false
}: {
  item: SidebarNavItem;
  isCurrentPath: (path: string) => boolean;
  handleNavigation: (path: string) => void;
  className?: string;
  size?: "default" | "small";
  dense?: boolean;
}) => {
  const isActive = Boolean(item.path && isCurrentPath(item.path));
  const sizeClasses = dense
    ? size === "small"
      ? "gap-2 px-3 py-1"
      : "gap-2 px-2 py-1.5"
    : size === "small"
      ? "gap-3 px-6 py-2"
      : "gap-3 px-3 py-2.5";

  const activeClasses = isActive
    ? size === "small"
      ? "bg-[rgba(var(--sidebar-nav-shadow-rgb),0.14)] text-white dark:bg-[rgba(var(--sidebar-nav-shadow-rgb),0.22)] dark:text-white"
      : "bg-[rgba(var(--sidebar-nav-shadow-rgb),0.08)] text-[color:var(--sidebar-nav-accent)] dark:bg-[rgba(var(--sidebar-nav-shadow-rgb),0.16)] dark:text-[color:var(--sidebar-nav-accent-dark)]"
    : className || "text-slate-700 hover:bg-slate-100/80 dark:text-zinc-300 dark:hover:bg-zinc-800";

  return (
    <button
      type="button"
      onClick={() => item.path && handleNavigation(item.path)}
      className={`group flex w-full items-center motion-safe:transition-all motion-safe:duration-200 ${
        dense ? "rounded-lg" : "rounded-xl"
      } ${sizeClasses} ${activeClasses}`}
      style={getToneVars(item.tone)}
    >
      <SidebarNavIcon
        icon={item.icon}
        active={isActive}
        compact={size === "small"}
        dense={dense}
        tone={item.tone}
      />
      <div className="min-w-0 flex-1 text-left">
        <div
          className={`truncate font-medium ${
            dense
              ? size === "small"
                ? "text-[11px]"
                : "text-xs"
              : "text-sm"
          }`}
        >
          {item.name}
        </div>
        {!dense && item.description && (
          <div className="text-xs text-slate-500 dark:text-zinc-400">
            {item.description}
          </div>
        )}
      </div>
    </button>
  );
};

// ─── CollapsibleSection ───────────────────────────────────────────────────────

const CollapsibleSection = ({
  item,
  isCollapsed,
  onToggleCollapse,
  isCurrentPath,
  handleNavigation,
  dense = false
}: {
  item: SidebarNavItem;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isCurrentPath: (path: string) => boolean;
  handleNavigation: (path: string) => void;
  dense?: boolean;
}) => {
  const hasActiveChild =
    item.children?.some((child) => child.path && isCurrentPath(child.path)) ??
    false;

  return (
    <div>
      <button
        type="button"
        onClick={onToggleCollapse}
        className={`group flex w-full items-center motion-safe:transition-all motion-safe:duration-200 ${
          dense ? "gap-2 rounded-lg px-2 py-1.5" : "gap-3 rounded-xl px-3 py-2.5"
        } ${
          hasActiveChild
            ? "bg-[rgba(var(--sidebar-nav-shadow-rgb),0.08)] text-[color:var(--sidebar-nav-accent)] dark:bg-[rgba(var(--sidebar-nav-shadow-rgb),0.16)] dark:text-[color:var(--sidebar-nav-accent-dark)]"
            : "text-slate-700 hover:bg-slate-100/80 dark:text-zinc-300 dark:hover:bg-zinc-800"
        }`}
        style={getToneVars(item.tone)}
      >
        <SidebarNavIcon icon={item.icon} active={hasActiveChild} dense={dense} tone={item.tone} />
        <div className="min-w-0 flex-1 text-left">
          <div
            className={`truncate font-medium ${dense ? "text-xs" : "text-sm"}`}
          >
            {item.name}
          </div>
          {!dense && item.description && (
            <div className="text-xs text-slate-500 dark:text-zinc-400">
              {item.description}
            </div>
          )}
        </div>
        <ChevronRight
          size={dense ? 14 : 16}
          className={`shrink-0 motion-safe:transition-transform motion-safe:duration-200 ${
            isCollapsed ? "rotate-0" : "rotate-90"
          }`}
        />
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isCollapsed
            ? "pointer-events-none max-h-0 opacity-0"
            : "max-h-[1100px] opacity-100"
        }`}
      >
        {!isCollapsed && item.children && (
          <div
            className={
              dense
                ? "mb-1 ml-4 mt-0.5 space-y-0.5 border-l border-slate-200/70 pb-1 pl-2.5 dark:border-zinc-800"
                : "mb-3 ml-6 mt-2 space-y-1 border-l border-slate-200/70 pb-3 pl-4 dark:border-zinc-800"
            }
          >
            {item.children.map((child) => (
              <NavButton
                key={child.id}
                item={child}
                isCurrentPath={isCurrentPath}
                handleNavigation={handleNavigation}
                className="text-[color:var(--sidebar-nav-child-accent)]/85 hover:bg-[rgba(var(--sidebar-nav-shadow-rgb),0.12)] dark:text-[color:var(--sidebar-nav-child-accent-dark)]/85 dark:hover:bg-[rgba(var(--sidebar-nav-shadow-rgb),0.20)]"
                size="small"
                dense={dense}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Panel lateral Explore (lista expandida con búsqueda) ─────────────────────

export interface SidebarExplorePanelProps {
  render?: boolean;
  /** Modo drawer móvil: ocupa el alto disponible del contenedor padre. */
  embedded?: boolean;
}

export const SidebarExplorePanel = React.memo(function SidebarExplorePanel({
  render = true,
  embedded = false
}: SidebarExplorePanelProps) {
  const router = useRouter();
  const { t } = useTranslation("common");
  const { topLevelNavItems } = useSidebarNavigation();
  const { closeBurgerSidebar } = useMobileSidebarStore();

  const [searchQuery, setSearchQuery] = useState("");
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    () =>
      new Set(
        topLevelNavItems
          .filter((item) => item.children?.length)
          .map((item) => item.id)
      )
  );

  const toggleSectionCollapse = (sectionId: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      next.has(sectionId) ? next.delete(sectionId) : next.add(sectionId);
      return next;
    });
  };

  const isCurrentPath = (path: string) =>
    router.pathname === path || router.pathname.startsWith(path);

  const handleNavigation = (path: string) => {
    closeBurgerSidebar();
    router.push(path);
  };

  // ── Search filter ─────────────────────────────────────────────────────────

  const filterItems = (items: SidebarNavItem[], query: string): SidebarNavItem[] => {
    if (!query) return items;
    return items
      .filter((item) => {
        const match =
          item.name.toLowerCase().includes(query.toLowerCase()) ||
          item.description?.toLowerCase().includes(query.toLowerCase());
        const childMatch = item.children?.some(
          (c) =>
            c.name.toLowerCase().includes(query.toLowerCase()) ||
            c.description?.toLowerCase().includes(query.toLowerCase())
        );
        return match || childMatch;
      })
      .map((item) => {
        if (!item.children) return item;
        const parentMatch =
          item.name.toLowerCase().includes(query.toLowerCase()) ||
          item.description?.toLowerCase().includes(query.toLowerCase());
        return {
          ...item,
          children: parentMatch
            ? item.children
            : item.children.filter(
                (c) =>
                  c.name.toLowerCase().includes(query.toLowerCase()) ||
                  c.description?.toLowerCase().includes(query.toLowerCase())
              )
        };
      });
  };

  const isSearching = searchQuery.trim().length > 0;
  const filteredNav = filterItems(topLevelNavItems, searchQuery);

  // Auto-expand solo al cambiar de ruta (no en cada render del menú).
  useEffect(() => {
    const sectionsWithActiveChildren = topLevelNavItems
      .filter((item) =>
        item.children?.some((child) => child.path && isCurrentPath(child.path))
      )
      .map((item) => item.id);

    if (sectionsWithActiveChildren.length === 0) return;

    setCollapsedSections((prev) => {
      const next = new Set(prev);
      sectionsWithActiveChildren.forEach((id) => next.delete(id));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al navegar
  }, [router.pathname]);

  if (!render) return null;

  return (
    <div
      className={
        embedded
          ? "flex min-h-0 flex-1 flex-col bg-transparent"
          : "sidebar-v2 z-[6010] grid content-start bg-transparent"
      }
    >
      {!embedded && (
        <div className="sticky top-0 z-10 rounded-xl bg-white px-3 pb-4 pt-4 dark:bg-zinc-900">
          <Input
            placeholder={t("Buscar")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftIcon={<Search size={16} />}
            size="compact"
          />
        </div>
      )}

      {/* Navigation */}
      <div
        className={
          embedded
            ? "min-h-0 flex-1 overflow-y-auto px-2 py-2 pb-4"
            : "overflow-y-auto px-3 pb-[120px]"
        }
      >
        <div className={embedded ? "sidebar-mobile-nav space-y-0.5" : "space-y-1"}>
          {!embedded && (
            <div className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-700/95 dark:text-zinc-200">
              {t("Navegación")}
            </div>
          )}
          {filteredNav.map((item, index) => (
            <div
              key={item.id}
              className={index === filteredNav.length - 1 && !embedded ? "mb-4" : ""}
            >
              {item.children?.length ? (
                <CollapsibleSection
                  item={item}
                  isCollapsed={!isSearching && collapsedSections.has(item.id)}
                  onToggleCollapse={() => toggleSectionCollapse(item.id)}
                  isCurrentPath={isCurrentPath}
                  handleNavigation={handleNavigation}
                  dense={embedded}
                />
              ) : (
                <NavButton
                  item={item}
                  isCurrentPath={isCurrentPath}
                  handleNavigation={handleNavigation}
                  dense={embedded}
                />
              )}
            </div>
          ))}
        </div>

        {/* Empty search state */}
        {!embedded && isSearching && filteredNav.length === 0 && (
            <div className="py-8 text-center">
              <Search size={32} className="mx-auto mb-3 text-slate-300 dark:text-zinc-600" />
              <div className="text-sm font-medium text-slate-600 dark:text-zinc-400">
                {t("No se encontraron resultados")}
              </div>
              <div className="mt-1 text-xs text-slate-400 dark:text-zinc-500">
                {t("Intenta con otros términos")}
              </div>
            </div>
          )}
      </div>

      <style jsx global>{`
        .sidebar-nav-icon-slot .sidebar-icon,
        .sidebar-nav-icon-slot img {
          width: 100% !important;
          height: 100% !important;
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          display: block;
        }
        .sidebar-icon {
          filter: brightness(0) saturate(100%) invert(0.4);
        }
        .dark .sidebar-icon {
          filter: brightness(0) saturate(100%) invert(0.48);
          opacity: 0.78;
        }
        .sidebar-mobile-nav .sidebar-icon {
          opacity: 1;
        }
        .text-indigo-600 .sidebar-icon,
        .text-indigo-400 .sidebar-icon {
          filter: brightness(0) saturate(100%) invert(0.3) sepia(1) saturate(3)
            hue-rotate(200deg);
        }
        .dark .text-indigo-400 .sidebar-icon {
          filter: brightness(0) saturate(100%) invert(0.5) sepia(1) saturate(2)
            hue-rotate(180deg);
        }
        .sidebar-v2 {
          height: calc(100dvh - var(--sidebar-header-mobile));
          margin: 1.5rem 0;
          border-radius: 18px;
          width: var(--sidebar-width-expanded);
          min-width: var(--sidebar-width-expanded);
          position: relative;
          overflow: hidden;
        }
        @media (min-width: 769px) {
          .sidebar-v2 {
            height: calc(100dvh - var(--sidebar-header-desktop) - 2.5rem);
          }
        }
      `}</style>
    </div>
  );
});
